import ArticleCollection from '@domain/catalog/ArticleCollection'
import { i18nLabelInterface } from '@domain/catalog/catalog.d'
import ApiCaller from '../utils/apiCaller'
import { getServiceURLserverSide } from '@domain/infrastructure/Urls'

export interface TechnicalData {
    baseColor: TechnicalDataColor[]
    color: TechnicalDataColor[]
    control: TechnicalDataAttribute[]
    cri: TechnicalDataAttribute[]
    installation: TechnicalDataAttribute[]
    material: TechnicalDataAttribute[]
    temperature: TechnicalDataAttribute[]
    colorMOQ: number
}

export type TechnicalDataAttribute = {
    description: i18nLabelInterface
    cri?: string
}
export type TechnicalDataColor = TechnicalDataAttribute & {
    label: string
    buttonColor: string
    ral: string
    hex?: string
    thumbnailImage?: string
}
class ArticleCollectionApi {
    constructor() { }

    static async getTechnicalInfo(subFamilyCode: number, catalogId: number, appLocation: number): Promise<TechnicalData> {
        return await ApiCaller.call('v2', 'GET', `articles/technicalInfo?subFamily=${subFamilyCode}&catalog=${catalogId}&appLocation=${appLocation}`)
    }

    static async getTechnicalInfoByModel(familyCode: number, catalogId: number, modelId: number): Promise<any> {
        return await ApiCaller.call('v2', 'GET', `articles/technicalInfoByModel?family=${familyCode}&catalog=${catalogId}&model=${modelId}`)

    }
    static async getSpotLights(subFamilyId: number, catalogId: number): Promise<any> {
        return await ApiCaller.call('v2', 'GET', `articles/technicalInfoByModel/spotlight?catalog=${catalogId}&subFamily=${subFamilyId}`)
    }

    static async getAttachments(familyId: number, model: number, applicationLocationId: number, data?: { application: number, location: number }): Promise<any> {
        if (!applicationLocationId && data) {
            const applicationLocation = await ApiCaller.call("v2", "GET", `applicationLocation?applicationId=${data.application}&locationId=${data.location}`)
            applicationLocationId = applicationLocation?.id
        }
        return await ApiCaller.call('v2', 'GET', `articles/technicalInfoByModel/attachments?family=${familyId}&model=${model}&appLocation=${applicationLocationId}`)
    }

    static async getModels(catalogId: number, applicationLocationId: number, subFamilyId: number, virtuals: 'only' | 'without' | 'included' = 'included'): Promise<any> {
        const virtualQuery = {
            only: 'virtual=1',
            without: 'virtual=0',
            included: ''
        }
        const query = [
            `catalogId=${catalogId}`,
            `applicationLocationId=${applicationLocationId}`,
            `subFamilyId=${subFamilyId}`,
            virtualQuery[virtuals]
        ].filter(q => q)
        return await ApiCaller.call('v2', 'GET', `articles/listModelsByFamily?${query.join('&')}`)
    }

    static composeParameters(subFamily: any, applicationLocation: any, family: number, model: number | string, productType: string, files: any[]) {
        type Params = {
            familyId: number | string;
            model: number | string;
            subFamilyId?: string;
            applicationLocationId: string;
            productType?: string;
            subFamily?: {
                id: number;
                description?: string;
            };
        };

        type Data = {
            type: string;
            subType?: string;
            params: Params;
        };
        const documentParams = files.map(type => {
            let documentType = type
            let data: Data = {
                type: documentType,
                params: {
                    familyId: family,
                    model: model,
                    subFamilyId: subFamily,
                    applicationLocationId: applicationLocation,
                    productType: productType
                }
            };
            switch (type) {
                case 'specSheet':
                    const modelType = Number(model);
                    data.subType = Math.floor(modelType) > 100 ? "model" : "umbrella";
                    delete data.params.subFamilyId;
                    data.params.subFamily = {
                        id: subFamily
                    }
                    break;
                case 'manual':
                    data.subType = 'REGULAR';
                    break;
                case 'ambient':
                    data.type = 'imagesHD';
                    data.subType = 'ambient';
                    break;
                case 'product':
                    data.type = 'imagesHD';
                    data.subType = 'product';
                    break;
                case 'energyLabel':
                    delete data.params.productType;
                    break;
                default:
                    break;
            }

            return data
        });
        return documentParams;
    }

    static async getDownloads(catalogId: string | number, subFamily: any, applicationLocation: any, family: number, model: number | string, token: string, productType: string, files: any[], language: string, requestSource: 'PUBLIC_AREA' | 'PRIVATE_AREA' | 'PUBLIC_AREA_DOWNLOAD' | 'PRIVATE_AREA_DOWNLOAD' = 'PUBLIC_AREA'): Promise<any> {
        const documentParams = this.composeParameters(subFamily, applicationLocation, family, model, productType, files);
        const lang = (language === 'en-US') ? 'us' : language;
        const serviceRoute = `documents/docs-web/global-generate?catalogId=${catalogId}&lang=${lang}&requestSource=${requestSource}`;
        return await ApiCaller.call('v2', 'POST', serviceRoute, documentParams, { credentials: 'include' });
    }

    static async getArticlesCollectionByModel(catalogId: string | number, model: string | number) {
        return await ApiCaller.call('v2', 'GET', `article/search?catalogId=${catalogId}&search=${model}`)
    }

    static async getArticleCollection(models, catalog, subfamily?: number): Promise<any> {
        //TODO: Use v3 api
        //  return await ApiCaller.call('', 'GET', `catalog/catalog_${catalog}/subFamily_${subFamily}/appLocation_${appLocation}.json`, null, null, 'static.')
        let url = `articles/variations?${models.map(model => `model=${model}`).join('&')}&catalog=${catalog}`

        if (subfamily) {
            url = `articles/variations?${models.map(model => `model=${model}`).join('&')}&catalog=${catalog}&subfamily=${subfamily}`
        }
        return await ApiCaller.call('v2', 'GET', url)
    }

    static async checkDownloadAvailability(
        catalogId: string | number,
        subFamilyId: string | number,
        applicationLocationId: number,
        model: string | number,
        lang: string
    ): Promise<Record<string, boolean>> {
        const query = [
            `catalogId=${catalogId}`,
            `subFamilyId=${subFamilyId}`,
            `applicationLocationId=${applicationLocationId}`,
            `model=${model}`,
            `lang=${lang}`,
        ].join('&')
        const url = `${getServiceURLserverSide('v3')}/documents/availability?${query}`
        const res = await fetch(url)
        // Throw on a non-2xx so callers can tell "no documents" from "the lookup broke".
        // Returning the error body used to leave the downloads list empty, which the UI
        // rendered as "nothing is downloadable".
        if (!res.ok) throw new Error(`documents/availability responded ${res.status}`)

        return res.json()
    }

    /**
     * Separated technical downloads (2D, 3D, BIM, dialux).
     *
     * A different endpoint from `getDownloads` because these are zips already hosted in
     * the CMS: the answer is a URL, there is nothing to generate. A format the model does
     * not have comes back with a `reason`, which is not an error — hence the 200.
     */
    static async getDownloadsByFormat(
        catalogId: string | number,
        subFamilyId: string | number,
        applicationLocationId: number,
        model: string | number,
        formats: string[],
        // Which surface asked, for the metric only. This modal opens from the public product
        // page and from the private area, and the endpoint cannot tell them apart on its own.
        requestSource?: string
    ): Promise<{ downloads: Array<{ format: string; url?: string; filename?: string; reason?: string }> }> {
        const url = `${getServiceURLserverSide('v3')}/documents/downloads/formats`
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ catalogId, subFamilyId, applicationLocationId, model, formats, requestSource }),
        })
        // Same guard as the two lookups below: on a non-2xx, res.json() would hand back
        // the error body and the caller would read its missing `downloads` as "no files".
        if (!res.ok) throw new Error(`documents/downloads/formats responded ${res.status}`)

        return res.json()
    }

    static async getCollectionCatalogUrl(
        catalogId: string | number,
        subFamilyId: string | number
    ): Promise<{ url: string | null }> {
        const query = `catalogId=${catalogId}&subFamilyId=${subFamilyId}`
        const url = `${getServiceURLserverSide('v3')}/documents/collection-catalog-url?${query}`
        const res = await fetch(url)
        // Same guard as checkDownloadAvailability above: on a non-2xx, res.json() used to
        // return the error body, whose missing `url` the caller read as "no catalog" —
        // indistinguishable from the lookup being broken.
        if (!res.ok) throw new Error(`documents/collection-catalog-url responded ${res.status}`)

        return res.json()
    }

    static async getArticleCollectionInstance({ catalog, applicationLocation, subfamily, includeVirtuals = false }) {
        const virtualsParam = includeVirtuals ? 'included' : 'without'
        const response = await ArticleCollectionApi.getModels(catalog, applicationLocation, subfamily, virtualsParam) || []

        if (response.message) {
            return
        }

        const models = response.find(elem => elem.catalogId == catalog)?.models || []
        const modelIds = models.map(model => model.model).sort((a, b) => a - b)

        const variations = await ArticleCollectionApi.getArticleCollection(modelIds, catalog, subfamily)
        if (!variations) return

        return ArticleCollection.getInstance(variations)
    }

}

export default ArticleCollectionApi